import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Filter, SlidersHorizontal, X } from "lucide-react";
import { format } from "date-fns";
import { cn, setDateToNoon } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getCompanies } from "@/services/getCompanies";
import { getCarServices } from "@/services/getCarServices";
import { fetchEmployees } from "@/services/getEmployees";
import {
  PaymentStatusMapping,
  OrderStatusMapping,
} from "../models/order.model";

export interface FilterValues {
  startDate?: Date;
  endDate?: Date;
  updatedStart?: Date;
  updatedEnd?: Date;
  paymentStatus?: string;
  status?: string;
  employee?: string;
  company?: string;
  plateNumber?: string;
  service?: string;
  addOns?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface OrdersFilterDialogProps {
  onFilterChange: (filters: FilterValues) => void;
  onClearFilters: () => void;
  currentFilters: FilterValues;
  isLoading?: boolean;
}

export function OrdersFilterDialog({
  onFilterChange,
  onClearFilters,
  currentFilters,
  isLoading = false,
}: OrdersFilterDialogProps) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValues>(currentFilters);

  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: getCompanies,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch services for the dropdown
  const { data: services, isLoading: servicesLoading } = useQuery({
    queryKey: ["car-services"],
    queryFn: getCarServices,
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return (
        data
          ?.map((service: { _id: string; name: string }) => ({
            _id: service._id,
            name: service.name,
          }))
          .sort((a: { name: string }, b: { name: string }) =>
            a.name.localeCompare(b.name)
          ) || []
      );
    },
  });

  const { data: employees, isLoading: employeesLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: fetchEmployees,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    select: (data) => {
      return (
        data
          ?.map(
            (employee: {
              _id: string;
              name?: string;
              fullName?: string;
              email: string;
            }) => ({
              _id: employee._id,
              name: employee.name || employee.fullName || employee.email,
            })
          )
          .sort((a: { name: string }, b: { name: string }) =>
            a.name.localeCompare(b.name)
          ) || []
      );
    },
  });

  const handleFilterChange = (
    key: keyof FilterValues,
    value: string | Date | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
    setOpen(false);
  };

  const handleClearFilters = () => {
    onClearFilters();
    setFilters({
      sortBy: "createdAt",
      sortOrder: "desc",
    });
    setOpen(false);
  };

  const hasActiveFilters = () => {
    return Object.entries(currentFilters).some(([key, value]) => {
      if (key === "sortBy" || key === "sortOrder") return false;
      return value !== undefined && value !== null && value !== "";
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="relative">
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filter
          {hasActiveFilters() && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filter Orders</DialogTitle>
          <DialogDescription>
            Set filters to narrow down the orders displayed in the table.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          <div className="space-y-2">
            <Label>Created Date Range</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal flex-1",
                      !filters.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate
                      ? format(filters.startDate, "PPP")
                      : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={setDateToNoon(filters.startDate)}
                    onSelect={(date) =>
                      handleFilterChange("startDate", setDateToNoon(date))
                    }
                    disabled={(date) => {
                      const todayNoon = setDateToNoon(new Date());
                      const minDateNoon = setDateToNoon(new Date("1900-01-01"));
                      return date > todayNoon || date < minDateNoon;
                    }}
                    captionLayout="dropdown"
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal flex-1",
                      !filters.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate
                      ? format(filters.endDate, "PPP")
                      : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={setDateToNoon(filters.endDate)}
                    onSelect={(date) =>
                      handleFilterChange("endDate", setDateToNoon(date))
                    }
                    disabled={(date) => {
                      const todayNoon = setDateToNoon(new Date());
                      const minDateNoon = setDateToNoon(new Date("1900-01-01"));
                      return date > todayNoon || date < minDateNoon;
                    }}
                    captionLayout="dropdown"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Updated Date Range</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal flex-1",
                      !filters.updatedStart && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.updatedStart
                      ? format(filters.updatedStart, "PPP")
                      : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={setDateToNoon(filters.updatedStart)}
                    onSelect={(date) =>
                      handleFilterChange("updatedStart", setDateToNoon(date))
                    }
                    disabled={(date) => {
                      const todayNoon = setDateToNoon(new Date());
                      const minDateNoon = setDateToNoon(new Date("1900-01-01"));
                      return date > todayNoon || date < minDateNoon;
                    }}
                    captionLayout="dropdown"
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal flex-1",
                      !filters.updatedEnd && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.updatedEnd
                      ? format(filters.updatedEnd, "PPP")
                      : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={setDateToNoon(filters.updatedEnd)}
                    onSelect={(date) =>
                      handleFilterChange("updatedEnd", setDateToNoon(date))
                    }
                    disabled={(date) => {
                      const todayNoon = setDateToNoon(new Date());
                      const minDateNoon = setDateToNoon(new Date("1900-01-01"));
                      return date > todayNoon || date < minDateNoon;
                    }}
                    captionLayout="dropdown"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentStatus">Payment Status</Label>
            <Select
              value={filters.paymentStatus || "all"}
              onValueChange={(value) =>
                handleFilterChange(
                  "paymentStatus",
                  value === "all" ? "" : value
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment Status</SelectItem>
                {Object.entries(PaymentStatusMapping).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Order Status</Label>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                handleFilterChange(
                  "status",
                  value === "all" ? "" : value
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select order status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Order Status</SelectItem>
                {Object.entries(OrderStatusMapping).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plateNumber">Plate Number</Label>
            <Input
              id="plateNumber"
              placeholder="Enter plate number"
              value={filters.plateNumber || ""}
              onChange={(e) =>
                handleFilterChange("plateNumber", e.target.value || undefined)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="service">Service</Label>
            <Select
              value={filters.service || "all"}
              onValueChange={(value) =>
                handleFilterChange(
                  "service",
                  value === "all" ? "" : value
                )
              }
              disabled={servicesLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    servicesLoading ? "Loading services..." : "Select service"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services?.map((service) => (
                  <SelectItem key={service._id} value={service._id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Select
              value={filters.company || "all"}
              onValueChange={(value) =>
                handleFilterChange(
                  "company",
                  value === "all" ? "" : value
                )
              }
              disabled={companiesLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    companiesLoading ? "Loading companies..." : "Select company"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies?.map((company) => (
                  <SelectItem key={company._id} value={company._id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="employee">Employee</Label>
            <Select
              value={filters.employee || "all"}
              onValueChange={(value) =>
                handleFilterChange(
                  "employee",
                  value === "all" ? "" : value
                )
              }
              disabled={employeesLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    employeesLoading
                      ? "Loading employees..."
                      : "Select employee"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees?.map((employee) => (
                  <SelectItem key={employee._id} value={employee._id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sortBy">Sort By</Label>
            <Select
              value={filters.sortBy || "createdAt"}
              onValueChange={(value) => handleFilterChange("sortBy", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sort field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Created Date</SelectItem>
                <SelectItem value="updatedAt">Updated Date</SelectItem>
                <SelectItem value="totalCost">Total Cost</SelectItem>
                <SelectItem value="plateNumber">Plate Number</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="paymentStatus">Payment Status</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Select
              value={filters.sortOrder || "desc"}
              onValueChange={(value) => handleFilterChange("sortOrder", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sort order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleClearFilters}>
            <X className="mr-2 h-4 w-4" />
            Clear All
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyFilters} disabled={isLoading}>
              {isLoading ? "Applying..." : "Apply Filters"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
